import React from 'react';
import { StyleSheet } from 'react-native';
import { DNABox, DNAButton, DNADivider, DNASpinner, Iffy } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/lib/theming/themes/alucio/colors';
import { PERFORMING_ACTION, SUBMITTING_STATUS } from 'src/components/EmailTemplateDrawer/EmailDrawerHelper';
import { EmailTemplate, EmailTemplateStatus } from '@alucio/aws-beacon-amplify/src/models';

const styles = StyleSheet.create({
  content: {
    backgroundColor: colors['color-text-white'],
    paddingHorizontal: 24,
    paddingVertical: 16,
  },
});

interface Props {
  canActivate?: boolean,
  closeDrawer: () => void,
  emailTemplate?: EmailTemplate,
  isFormDirty?: boolean,
  onActivate: (action: PERFORMING_ACTION) => void,
  submittingStatus: SUBMITTING_STATUS,
  onPreview: () => void,
  onDelete: () => void,
}

const EmailDrawerFooter = (props: Props) => {
  const isSuccess = props.submittingStatus === SUBMITTING_STATUS.SUCCESS;
  const isSubmitting = props.submittingStatus === SUBMITTING_STATUS.SUBMITTING;

  function onActivate(): void {
    props.onActivate(PERFORMING_ACTION.ACTIVATING);
  }

  function onSaveDraft(): void {
    props.onActivate(props.emailTemplate ? PERFORMING_ACTION.SAVE_EXISTING : PERFORMING_ACTION.FIRST_DRAFT);
  }

  function updateTemplate(): void {
    props.onActivate(PERFORMING_ACTION.SAVE_EXISTING);
  }

  function onDelete(): void {
    props.onDelete()
  }

  return (
    <DNABox appearance="col" alignY="end">
      <DNADivider />
      <DNABox spacing="between" alignY="center" style={styles.content}>
        <DNABox spacing="xs" fill>
          <Iffy is={props.emailTemplate?.id}>
            <DNAButton
              status="tertiary"
              iconRight="trash"
              onPress={onDelete}
              disabled={!props.emailTemplate}
            />
          </Iffy>
          <DNAButton
            testID="email-template-preview-button"
            status="tertiary"
            onPress={props.onPreview}
            children="Preview"
          />
        </DNABox>
        <DNABox spacing="sm">
          <DNAButton
            testID="email-template-cancel-button"
            status="tertiary"
            onPress={props.closeDrawer}
            children="Cancel"
          />
          <Iffy is={!props.emailTemplate?.status || props.emailTemplate?.status === EmailTemplateStatus.DRAFT}>
            <DNAButton
              testID="email-template-draft-button"
              appearance={isSuccess ? 'filled' : 'outline'}
              {...(isSuccess) ? { status: 'success', iconLeft: 'check' } : {}}
              disabled={!props.isFormDirty && !isSuccess}
              onPress={onSaveDraft}
              style={{ maxHeight: 43 }}
            >
              {
                isSuccess ? 'Saved' : isSubmitting ? <DNASpinner size="xs" status="info" /> : 'Save draft'
              }
            </DNAButton>
          </Iffy>
          <Iffy is={props.emailTemplate?.status !== EmailTemplateStatus.ACTIVE}>
            <DNAButton
              testID="email-template-activate-button"
              disabled={!props.canActivate}
              onPress={onActivate}
              children="Activate"
            />
          </Iffy>
          <Iffy is={props.emailTemplate?.status === EmailTemplateStatus.ACTIVE}>
            <DNAButton
              testID="email-template-activate-button"
              {...(isSuccess) ? { status: 'success', iconLeft: 'check' } : {}}
              onPress={updateTemplate}
              disabled={!props.canActivate || !props.isFormDirty}
              style={{ maxHeight: 43 }}
            >
              {
                isSuccess ? 'Updated' : isSubmitting ? <DNASpinner size="xs" status="info" /> : 'Update template'
              }
            </DNAButton>
          </Iffy>
        </DNABox>
      </DNABox>
    </DNABox>
  );
};

EmailDrawerFooter.displayName = 'EmailDrawerFooter';

export default EmailDrawerFooter;
